import React from "react";
import { Box } from "@mui/material";
import { useWindowSize } from "@uidotdev/usehooks";
import SabiasQue from "../SabiasQue";

// Template175: Barrios 1 - bloques separados (hero, intro, paintedLadies, haightAshbury, llegar, consejo)
const Template175 = ({
	backgroundColor,
	textColor,
	headerBackgroundColor,
	headerTextColor,
	fontFamily,
	page,
}) => {
	const isSmallScreen = useWindowSize().width <= 600;

	const getImageSrc = (id) =>
		page.images?.find((img) => img.id === id)?.src ?? "";
	const getCanvasImageUrl = (id) =>
		page.canvasImages?.find((img) => img.id === id)?.imageUrl ?? "";

	const headerText = page.texts?.[0]?.content ?? "";
	const introText = page.texts?.[1]?.content ?? "";
	const logoImage = getCanvasImageUrl("logo");
	const hero = page?.hero ?? page?.data?.hero ?? page?.data?.data?.hero ?? {};
	const heroImageSrc = getImageSrc(hero.imageId);
	const heroStickerSrc = getImageSrc(hero.stickerImageId);
	const sections = Array.isArray(page?.sections)
		? page.sections
		: Array.isArray(page?.data?.sections)
			? page.data.sections
			: Array.isArray(page?.data?.data?.sections)
				? page.data.data.sections
				: [];
	const paintedLadies = sections[0] ?? {};
	const haightAshbury = sections[1] ?? {};
	const llegar = page?.llegar ??
		page?.data?.llegar ??
		page?.data?.data?.llegar ?? { title: "", items: [] };
	const consejo = page?.consejo ??
		page?.data?.consejo ??
		page?.data?.data?.consejo ?? { text: "" };

	const contentColor = textColor || "#444";

	const estiloH3 = {
		fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
		fontSize: 14,
		fontWeight: 700,
		textTransform: "uppercase",
		margin: 0,
	};
	const estiloP = {
		fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
		fontSize: 13,
		color: contentColor,
		margin: 0,
	};

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				height: "100%",
				backgroundColor: backgroundColor || "#f7f7f7",
				color: contentColor,
				position: "relative",
				fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
				"& *:not([data-template-header])": {
					fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
					color: "inherit",
				},
			}}
		>
			<Box
				data-block-id="header"
				data-template-header
				sx={{
					backgroundColor: headerBackgroundColor || "#F2B705",
					color: headerTextColor || "#fff",
					textAlign: "center",
					padding: "15px 0",
					fontFamily: "'Poppins', sans-serif",
					fontSize: "14px",
					fontWeight: 600,
					flexShrink: 0,
					position: "sticky",
					top: 0,
					zIndex: 9999,
				}}
			>
				{headerText}
				{logoImage && (
					<Box
						component="img"
						src={logoImage}
						alt="logo"
						sx={{
							width: isSmallScreen ? 66 : 80,
							position: "absolute",
							right: 2,
							top: 18,
							zIndex: 999999,
						}}
					/>
				)}
			</Box>

			{/* ——— Hero (portada barrios) ——— */}
			{heroImageSrc && (
				<Box data-block-id="image-0" sx={{ position: "relative" }}>
					<Box
						component="img"
						src={heroImageSrc}
						alt="Painted Ladies"
						sx={{ width: "100%", height: 200, objectFit: "cover" }}
					/>
					{hero.title && (
						<Box
							component="h1"
							sx={{
								fontFamily: "'Poppins', sans-serif",
								fontSize: 32,
								textAlign: "center",
								position: "absolute",
								bottom: 0,
								margin: "10px",
								left: 20,
								color: "#fff",
								textShadow: "2px 2px 4px rgba(0,0,0,0.6)",
							}}
						>
							{hero.title}
						</Box>
					)}
					{heroStickerSrc && (
						<Box
							component="img"
							src={heroStickerSrc}
							alt="casas"
							sx={{ width: 100, position: "absolute", bottom: 55, left: 21 }}
						/>
					)}
				</Box>
			)}

			{/* ——— Intro ——— */}
			{introText && (
				<Box data-block-id="text-1" sx={{ padding: "10px 12px" }}>
					<Box
						component="p"
						sx={{
							fontFamily: "'Poppins', sans-serif",
							fontSize: 13,
							color: "#333",
							margin: 0,
							padding: 0,
						}}
					>
						{introText}
					</Box>
				</Box>
			)}

			<Box sx={{ padding: "10px 12px" }}>
				<Box
					sx={{
						flex: 1,
						display: "flex",
						flexDirection: "column",
						gap: 2,
						position: "relative",
					}}
				>
					{/* ——— Painted Ladies (Alamo Square) ——— */}
					<Box
						data-block-id="text-2"
						sx={{
							display: "flex",
							gap: 2.5,
							alignItems: "flex-start",
							marginTop: isSmallScreen ? 0 : "-10px",
							position: "relative",
						}}
					>
						{getImageSrc(paintedLadies.stickerImageId) && (
							<Box
								component="img"
								src={getImageSrc(paintedLadies.stickerImageId)}
								alt=""
								sx={{
									width: 100,
									position: "absolute",
									bottom: isSmallScreen ? -240 : -50,
									right: isSmallScreen ? "auto" : 0,
									left: isSmallScreen ? -20 : "auto",
									zIndex: 9999,
								}}
							/>
						)}
						{paintedLadies.tooltipText && (
							<Box
								sx={{
									position: "absolute",
									bottom: isSmallScreen ? -180 : -40,
									right: isSmallScreen ? "auto" : 100,
									left: isSmallScreen ? 10 : "auto",
									backgroundColor: "#fff8db",
									border: "2px solid #f5c400",
									borderRadius: isSmallScreen
										? "16px 16px 16px 0px"
										: "16px 16px 0px 16px",
									padding: "10px 12px",
									fontSize: 11,
									fontFamily: "'Poppins', sans-serif",
									color: contentColor,
									boxShadow: "2px 2px 4px rgba(0,0,0,0.15)",
									maxWidth: 200,
									zIndex: 9999,
								}}
							>
								{paintedLadies.tooltipText}
							</Box>
						)}
						{getImageSrc(paintedLadies.imageId) && (
							<Box
								component="img"
								src={getImageSrc(paintedLadies.imageId)}
								alt=""
								sx={{
									width: "40%",
									borderRadius: "12px",
									objectFit: "cover",
									maxHeight: 110,
								}}
							/>
						)}
						<Box sx={{ width: "60%", position: "relative" }}>
							{paintedLadies.title && (
								<Box
									component="h3"
									sx={{
										fontFamily: "'Poppins', sans-serif",
										marginBottom: 0.5,
										fontSize: 14,
										fontWeight: 700,
										textTransform: "uppercase",
										marginTop: 1,
									}}
								>
									{paintedLadies.title}
								</Box>
							)}
							{paintedLadies.body && (
								<Box
									component="p"
									sx={{
										fontSize: 13,
										color: contentColor,
										fontFamily: "'Poppins', sans-serif",
										margin: 0,
									}}
								>
									{paintedLadies.body}
								</Box>
							)}
						</Box>
					</Box>

					{/* ——— Haight-Ashbury ——— */}
					<Box data-block-id="text-3" sx={{ display: "flex", position: "relative" }}>
						<Box sx={{ width: "100%" }}>
							<Box
								sx={{
									...estiloP,
									padding: "20px",
									backgroundColor: "#fbf0cf",
									borderRadius: "12px",
								}}
							>
								{haightAshbury.title && (
									<Box component="h3" sx={estiloH3}>
										{haightAshbury.title}
									</Box>
								)}
								{haightAshbury.body && (
									<Box component="span" sx={{ display: "block" }}>
										{haightAshbury.body}
									</Box>
								)}
							</Box>
						</Box>
						{haightAshbury.sabiasQue?.[0] && (
							<Box
								sx={{
									position: "absolute",
									bottom: isSmallScreen ? -80 : -20,
									right: "50%",
								}}
							>
								<SabiasQue
									icono={haightAshbury.sabiasQue[0].icono}
									texto={haightAshbury.sabiasQue[0].texto}
								/>
							</Box>
						)}
					</Box>

					{/* ——— Cómo llegar + Consejo (círculo) ——— */}
					<Box
						data-block-id="text-4"
						sx={{
							position: "relative",
							marginTop: isSmallScreen ? 0 : "-10px",
						}}
					>
						{llegar.title && (
							<Box
								sx={{
									backgroundColor: "#fbf0cf",
									border: "3px solid #F2B705",
									borderRadius: "12px",
									padding: "16px 20px",
									fontSize: 12,
									fontFamily: "'Poppins', sans-serif",
									color: "#333",
									width: isSmallScreen ? "100%" : "100%",
									marginTop: isSmallScreen ? 10 : 5,
								}}
							>
								<Box
									component="h4"
									sx={{
										margin: 0,
										marginBottom: 1,
										fontSize: 13,
										fontWeight: 700,
										display: "flex",
										alignItems: "center",
										gap: 0.75,
										color: "#ca9904",
										textTransform: "uppercase",
									}}
								>
									{llegar.title}
								</Box>
								<Box
									component="ul"
									sx={{ paddingLeft: 2.5, margin: 0, lineHeight: 1.5 }}
								>
									{llegar.items?.map((item, i) => (
										<li
											key={i}
											dangerouslySetInnerHTML={{
												__html:
													(item.label
														? `<strong>${item.label}</strong> `
														: "") + (item.text ?? ""),
											}}
										/>
									))}
								</Box>
							</Box>
						)}
						{consejo.text && (
							<Box
								sx={{
									position: "absolute",
									bottom: isSmallScreen ? 120 : 60,
									right: 0,
								}}
							>
								<Box
									sx={{
										backgroundColor: "#F2B705",
										border: "6px solid #ca9904",
										padding: 2,
										fontSize: 9,
										borderRadius: "50%",
										width: 130,
										height: 130,
										color: "white",
										textAlign: "center",
										display: "flex",
										justifyContent: "center",
										flexDirection: "column",
										alignItems: "center",
										fontFamily: "'Poppins', sans-serif",
										marginTop: isSmallScreen ? 0 : 0,
									}}
								>
									{consejo.text}
								</Box>
							</Box>
						)}
					</Box>
				</Box>
			</Box>
		</Box>
	);
};

Template175.thumbnail = "/images/template-1.png";
export default Template175;
